import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { authGoogleExchange } from '@/lib/api';

export default function AuthCallback() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const params = new URLSearchParams(hash.replace('#', ''));
      const accessToken = params.get('access_token');
      const oauthError = params.get('error_description') || params.get('error');

      if (!accessToken) {
        const savedMode = localStorage.getItem('oauth_mode');
        localStorage.removeItem('oauth_mode');
        const msg = oauthError || 'Google sign-in was cancelled or failed. Please try again.';
        sessionStorage.setItem('auth_error', msg);
        window.location.replace(savedMode === 'signup' ? '/signup' : '/login');
        return;
      }

      // Clear sensitive token from URL now that we have extracted it
      window.history.replaceState(null, '', window.location.pathname);

      const mode = localStorage.getItem('oauth_mode') || 'login';
      // Keep oauth_mode until after the API call — AuthContext reads it
      // synchronously to decide whether to skip authMe.

      try {
        const res = await authGoogleExchange(accessToken, mode);
        localStorage.removeItem('oauth_mode');

        if (res.data.requires_otp) {
          navigate('/verify-otp', {
            replace: true,
            state: { email: res.data.email, from: 'google' },
          });
          return;
        }

        login(res.data);
        navigate('/dashboard', { replace: true });
      } catch (err) {
        const savedMode = localStorage.getItem('oauth_mode');
        localStorage.removeItem('oauth_mode');
        const detail = err.response?.data?.detail || 'Authentication failed. Please try again.';
        sessionStorage.setItem('auth_error', detail);
        window.location.replace(savedMode === 'signup' ? '/signup' : '/login');
      }
    };

    processAuth();
  }, [navigate, login]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-paper">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-ink-subtle border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-small text-ink-muted">Signing you in…</p>
      </div>
    </div>
  );
}
